import { ExtendedProductDetailProduct } from 'shared/types/product/Product';
import { ExtendedVariant } from 'shared/types/product/Variant';
import { textToColor } from 'helpers/textToColor/textToColor';
import { getAxisOptionValues, getSelectableAxes } from './axisValues';
import { toTagSlug } from './getVariantSpecies';
import { sortAxisValues } from './optionAxes';
import { findVariantSkuForSelection, VariantAttributeGroup } from './variantMatching';

const COLOR_AXIS = 'color';

// `textToColor` hands back the raw text when it can't resolve a color ("Black & Bronze"); only a real
// hex may reach the tile's background or the SVG swatch.
const hexOrNull = (code: string) => (code.startsWith('#') ? code : null);

export type ColorSwatch = {
  /** `finish:<key>` tag value for the swatch photo, e.g. `standard-black`. */
  finishKey: string;
  name: string;
  hex: string | null;
  /** Colors reachable from the current choices on every other axis, each with the SKU it selects. */
  options: Array<{ name: string; hex: string | null; sku: string }>;
};

/**
 * Swatch data for products whose colors are a variant axis (Express: one SKU per Standard Black /
 * Silver / White) rather than a finish picked on top of a SKU (Signature). Both draw their swatch
 * photo from the same `finish:<key>` tag, so the color label slugs to the finish key
 * ("Standard Black:#000000" → `standard-black`).
 *
 * Returns undefined when the product has finishes (the finish picker owns the swatch) or when
 * color isn't a real choice. A chip only appears for a color with a SKU behind it given the
 * other axes, using the buy box's matcher, so a tap can never land on a combination the buy box
 * would refuse.
 */
export const getColorSwatch = (
  product: ExtendedProductDetailProduct,
  variant: ExtendedVariant,
  hasFinishes: boolean,
): ColorSwatch | undefined => {
  if (hasFinishes) return undefined;

  // Same axis set as the buy box, so a chip selects exactly what the color buttons would.
  const selectableAxes = getSelectableAxes(product, variant.sku);
  const colorAxis = selectableAxes.find((attribute) => attribute.key === COLOR_AXIS);
  const current = variant.attributes?.[COLOR_AXIS];
  if (!colorAxis || typeof current !== 'string') return undefined;
  const axisKeys = selectableAxes.map((attribute) => attribute.key);
  const selection = Object.fromEntries(
    axisKeys.map((key) => [key, variant.attributes?.[key] as string | number | undefined]),
  );
  const groups = product.variantDefiningAttributeGroups as VariantAttributeGroup[] | undefined;

  const { label, code } = textToColor(current);
  // One chip per label, in the buy box's order, so the row never reshuffles as the selection moves:
  // chips select by label, so two values sharing one ("Standard Black" with two hex codes) would
  // otherwise select the same SKU. The current variant's own value wins its label's slot.
  const options = new Map<string, ColorSwatch['options'][number]>();
  sortAxisValues(COLOR_AXIS, getAxisOptionValues(product, variant.sku, colorAxis)).forEach((value) => {
    const option = textToColor(value);
    if (options.has(option.label)) return;
    if (option.label === label) {
      options.set(label, { name: label, hex: hexOrNull(code), sku: variant.sku });
      return;
    }
    const sku = findVariantSkuForSelection(groups, { ...selection, [COLOR_AXIS]: value }, axisKeys);
    if (sku) options.set(option.label, { name: option.label, hex: hexOrNull(option.code), sku });
  });
  if (!options.has(label)) options.set(label, { name: label, hex: hexOrNull(code), sku: variant.sku });

  return { finishKey: toTagSlug(label), name: label, hex: hexOrNull(code), options: [...options.values()] };
};
